import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { trackEvent } from '@/utils/trackingManager';

const MAX_ERROR_MESSAGE_LENGTH = 240;

export function getAuthDiagnosticErrorMessage(error: unknown): string {
  let message = 'Unknown auth error';

  if (error instanceof Error && error.message) {
    message = error.message;
  } else if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string' &&
    (error as { message: string }).message
  ) {
    message = (error as { message: string }).message;
  } else if (typeof error === 'string' && error.trim()) {
    message = error;
  }

  return message.length > MAX_ERROR_MESSAGE_LENGTH
    ? message.slice(0, MAX_ERROR_MESSAGE_LENGTH)
    : message;
}

function getCurrentAuthPath(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.pathname || null;
}

async function getNativeAppInfo(isNativeRuntime: boolean) {
  if (!isNativeRuntime) {
    return {
      app_version: null,
      app_build: null,
      app_name: null,
    };
  }

  try {
    const appInfo = await CapacitorApp.getInfo();
    return {
      app_version: appInfo.version || null,
      app_build: appInfo.build || null,
      app_name: appInfo.name || null,
    };
  } catch {
    return {
      app_version: null,
      app_build: null,
      app_name: null,
    };
  }
}

export async function buildAuthDiagnosticPayload(
  params: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const isNativeRuntime = Capacitor.isNativePlatform();
  const platform = isNativeRuntime ? Capacitor.getPlatform() : 'web';
  const appInfo = await getNativeAppInfo(isNativeRuntime);

  return {
    diagnostic_area: 'auth',
    is_native_runtime: isNativeRuntime,
    platform,
    route_path: getCurrentAuthPath(),
    ...appInfo,
    ...params,
  };
}

export function trackAuthDiagnosticEvent(
  eventName: string,
  params: Record<string, unknown> = {},
): void {
  void buildAuthDiagnosticPayload(params)
    .then((payload) => trackEvent(eventName, payload, { serverOnly: true }))
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.warn('Auth diagnostic tracking failed:', error);
      }
    });
}
